import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCheckCircle, FaExclamationCircle, FaSpinner, FaArrowLeft, FaLock } from 'react-icons/fa';
import logoArrows from '../images/logo_arrows.svg';

const ResetPassword = () => {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  
  const navigate = useNavigate();

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*()€,.?":;{}|<>-_]/.test(password)) return 'Password must contain at least one special character';
    return '';
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    setPasswordStrength(validatePassword(pwd));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setStatus('error');
      setMessage('Missing reset token');
      return;
    }
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage("Passwords don't match");
      return;
    }
    
    const strengthError = validatePassword(newPassword);
    if (strengthError) {
      setStatus('error');
      setMessage(strengthError);
      return;
    }
    
    setStatus('loading');
    setMessage('Resetting your password...');

    try {
      const response = await fetch('https://easysubs-1ad308994ada.herokuapp.com/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Password reset successfully!');
      } else {
        setStatus('error');
        setMessage(data.error || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#253985] to-[#1a2a6c] text-white py-8 px-6 text-center relative">
          <button 
            onClick={() => navigate('/')}
            className="absolute left-4 top-6 text-white hover:text-gray-200 transition-colors"
          >
            <FaArrowLeft className="text-xl" />
          </button>
          <div className="flex justify-center mb-4">
            <img src={logoArrows} alt="EasySubs Logo" className="h-16" />
          </div>
          <h1 className="text-2xl font-bold">Reset Password</h1>
          <p className="text-blue-100 mt-2">Set a new password for your account</p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {status === 'form' && (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password"
                    required
                  />
                  <FaLock className="absolute right-3 top-3 text-gray-400" />
                </div>
                {passwordStrength && (
                  <p className="text-xs text-red-500 mt-1">{passwordStrength}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters, with uppercase, lowercase, number, and symbol.
                </p>
              </div>
              
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                    required
                  />
                  <FaLock className="absolute right-3 top-3 text-gray-400" />
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-[#253985] to-[#1a2a6c] hover:from-[#1a2a6c] hover:to-[#253985] transition-all duration-300 shadow-md"
              >
                Reset Password
              </button>
            </form>
          )}
          
          {status === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <FaSpinner className="text-3xl text-blue-600 animate-spin" />
                </div>
              </div>
              <p className="text-gray-700 text-center">{message}</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <FaCheckCircle className="text-4xl text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Password Reset!</h2>
              <p className="text-gray-600 text-center mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 transition-all duration-300 shadow-md"
              >
                Go to Login
              </button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <FaExclamationCircle className="text-4xl text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
              <p className="text-gray-600 text-center mb-6">
                {message}
              </p>
              {message === 'Password reset failed' && (
                <p className="text-gray-600 text-center mb-6">
                  The link may have expired or is invalid. Please request a new reset link.
                </p>
              )}
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-[#253985] to-[#1a2a6c] hover:from-[#1a2a6c] hover:to-[#253985] transition-all duration-300 shadow-md"
              >
                Request New Reset Link
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-8 text-center text-gray-500 text-sm">
        <p>© {new Date().getFullYear()} EasySubs. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ResetPassword;